import React, {createContext, useEffect, useMemo, useState} from 'react';
import './App.css';
import {ProjectInformationComponent} from "./ProjectInformation";
import {
    BuildingType,
    GreenLoanResult,
    GreenLoanService,
    IncludedMeasure,
    Measure,
    ProjectInformation
} from "../services/GreenLoanService";
import {GreenLoanResultComponent} from "./GreenLoanResult";
import {Box, Grid, ThemeProvider, Typography} from "@mui/material";
import globalTheme from "../GlobalTheme";
import {MeasureGroup} from "./MeasureGroup";

type Context = {
    handleIncludedMeasureAdd: (newIncludedMeasure: IncludedMeasure) => void,
    handleIncludedMeasureRemove: (measureNumber: number, hasBeenImplemented: boolean) => void,
    handleIncludedMeasureChange: (measureNumber: number, includedMeasure: IncludedMeasure) => boolean,
    projectInformation: ProjectInformation,
    setProjectInformation: (projectInformation: ProjectInformation) => void,
    currentYear: number
}

export const GreenLoanContext = createContext<Context>({} as Context)

function App() {
    const [measures, setMeasures] = useState<Measure[]>([])
    const [greenLoanResult, setGreenLoanResult] = useState<GreenLoanResult>({
        achievedSavings: 0,
        achievedEnergyMarking: "F",
        achievedEnergySavingsKwh: 0
    })
    const [includedMeasures, setIncludedMeasures] = useState<IncludedMeasure[]>([])
    const [projectInformation, setProjectInformation] = useState<ProjectInformation>({
        buildYear: 1952,
        buildingType: BuildingType.ApartmentBlock,
        area: 100,
        energySupply: "Electricity"
    })
    const currentYear = useMemo(() => new Date().getFullYear(), [])
    const [buildYearMax, setBuildYearMax] = useState<number>(currentYear)

    function handleIncludedMeasureAdd(newIncludedMeasure: IncludedMeasure) {
        setIncludedMeasures([...includedMeasures, newIncludedMeasure])
    }

    function handleIncludedMeasureRemove(measureNumber: number, isOldImplementation: boolean) {
        const newIncludedMeasures = includedMeasures.filter(measure => !(measure.measureNumber === measureNumber && measure.isOldImplementation === isOldImplementation));
        if (newIncludedMeasures.length !== includedMeasures.length) {
            setIncludedMeasures(newIncludedMeasures)
        }
    }

    function handleIncludedMeasureChange(measureNumber: number, includedMeasure: IncludedMeasure): boolean {
        const newIncludedMeasures = [...includedMeasures]
        const index = newIncludedMeasures.findIndex(m => m.measureNumber === measureNumber && m.isOldImplementation === includedMeasure.isOldImplementation)
        let measureFound = index !== -1;
        if (measureFound) {
            newIncludedMeasures[index] = includedMeasure
            setIncludedMeasures(newIncludedMeasures)
            return true
        }
        return false
    }

    function getMinImplementationYear(): number | null {
        let minYear: number | null = null
        includedMeasures
            .filter(measure => measure.implementationYear !== null)
            .forEach(measure => {
                if (minYear === null) {
                    minYear = measure.implementationYear!!
                } else {
                    if (minYear > measure.implementationYear!!) {
                        minYear = measure.implementationYear
                    }
                }
            })
        return minYear
    }

    function updateMaxBuildYear() {
        const minImplementationYear = getMinImplementationYear();
        if (minImplementationYear !== null) {
            setBuildYearMax(minImplementationYear - 1)
        } else {
            setBuildYearMax(currentYear)
        }
    }

    const greenLoanContextValue: Context = {
        handleIncludedMeasureAdd: handleIncludedMeasureAdd,
        handleIncludedMeasureRemove: handleIncludedMeasureRemove,
        handleIncludedMeasureChange: handleIncludedMeasureChange,
        projectInformation: projectInformation,
        setProjectInformation: setProjectInformation,
        currentYear: currentYear
    }

    const greenLoanService = new GreenLoanService()

    useEffect(() => {
        greenLoanService.getMeasures().then(res =>
            setMeasures(res.data)
        )
    }, [])

    useEffect(() => {
        updateMaxBuildYear()
        greenLoanService.calculateLoan(
            {
                includedMeasures: includedMeasures,
                projectInformation: projectInformation
            }
        )
            .then(res => setGreenLoanResult(res.data))
    }, [includedMeasures, projectInformation])

    return (
        <ThemeProvider theme={globalTheme}>
            <div className="App" style={{margin: "0 auto", maxWidth: "1000px", width: "100%"}}>
                <GreenLoanContext.Provider value={greenLoanContextValue}>
                    <div style={{marginBottom: "50px", marginTop: "40px"}}>
                        <Typography>
                            Kalkulator for
                        </Typography>
                        <Typography variant="h1" color="primary" fontSize="2.5rem">
                            Grønt energilån
                        </Typography>
                        <Typography sx={{whiteSpace: "pre-line"}}>
                            {"Søk om grønt energilån ved å gjøre bygningen din mer energieffektiv.\n " +
                                "Fyll inn info om bygg og tiltak og se effekten det vil ha."}
                        </Typography>
                    </div>
                    <Grid container className="content" columnGap="10px">
                        <Grid item style={{flex: "1 0 300px"}}>
                            <ProjectInformationComponent projectInformation={projectInformation}
                                                         buildYearMax={buildYearMax}/>
                            <MeasureGroup measures={measures}/>
                        </Grid>
                        <Grid item style={{flex: "1 0 auto"}}>
                            <GreenLoanResultComponent greenLoanResult={greenLoanResult}/>
                        </Grid>
                    </Grid>
                    <Box sx={{marginTop: "30px"}}>
                        <Typography>
                            Powered by
                        </Typography>
                        <img src={process.env.PUBLIC_URL + '/logo.jpg'} alt="logo" width={100} height={20}/>
                    </Box>

                </GreenLoanContext.Provider>
            </div>
        </ThemeProvider>
    );
}

export default App;
